import React, { useEffect, useState } from 'react'
import { Check, Plus, X, XCircle } from 'react-feather'
import { useLocale } from 'src/lib/hooks/useLocal'
import {
  utmDataOptions,
  UTMSelectorDropdownOptionType,
  UTMSelectorStateType
} from 'src/lib/types/common/customerConditions'
import FloGenericDialog from '../../feedback-and-dialog/GenericDialog'
import produce from 'immer'
import { utmDataDropdownOptions } from 'src/assets/CustomerConditionsConfig'
import DropdownButton from '../../form-elements/DropdownButton'
import InputTextField from '../../form-elements/InputTextField'
import { Chip } from '@mui/material'
import { FlowErrorType } from 'src/lib/types/common/error'

interface UTMSelectorProps {
  state: UTMSelectorStateType[]
  setState: (utmData: UTMSelectorStateType[]) => void
  errorState: FlowErrorType
  setErrorState: (errorState: FlowErrorType) => void
}

const UTMSelector: React.FC<UTMSelectorProps> = ({ state, setState, errorState, setErrorState }) => {
  const [isSelectorDialogOpen, setIsSelectorDialogOpen] = useState<boolean>(false)
  const [utmData, setUtmData] = useState<UTMSelectorStateType[]>([])
  const [utmErrorState, setUtmErrorState] = useState<FlowErrorType[]>([])
  const { t } = useLocale()

  useEffect(() => {
    if (state.length > 0) {
      setUtmData(state)
      setUtmErrorState(
        state.map(item => {
          return {
            status: false,
            message: ''
          }
        })
      )
      return
    }
    setUtmData([{ key: utmDataOptions.UTM_SOURCE, value: [] }])
    setUtmErrorState([{ status: false, message: '' }])
  }, [state])

  return (
    <div className='flex flex-col space-y-2'>
      {state.map((utmItem: UTMSelectorStateType) => {
        return (
          <div className='flex flex-wrap w-full h-full gap-2 mt-3' key={`${utmItem?.key}`}>
            {utmItem?.value?.map((value: string, valueIndex: number) => (
              <Chip
                key={`${value}`}
                style={{
                  borderRadius: '4px',
                  border: '1px solid #F0F0F0',
                  background: '#FAFAFA',
                  fontSize: '14px',
                  color: '#6e6e6e',
                  height: '25px'
                }}
                size='medium'
                label={value}
              />
            ))}
          </div>
        )
      })}
      <button
        className='text-sm font-medium text-primary-dark-color px-3 py-2 rounded-xl border w-fit'
        onClick={() => {
          setErrorState({ status: false, message: '' })
          setIsSelectorDialogOpen(true)
        }}
      >
        <span className='flex flex-row items-center gap-2'>
          {t('select_utm_data')}
          <Plus size={16} />
        </span>
      </button>

      {errorState.status && <p className='text-sm text-red-dark'>{errorState.message}</p>}
      <FloGenericDialog
        isOpen={isSelectorDialogOpen}
        handleClose={() => {
          setUtmData(state)
          setIsSelectorDialogOpen(false)
        }}
        title={t('select_utm_data')}
        content={
          <UTMSelectorDialog
            utmData={utmData}
            setUtmData={setUtmData}
            utmErrorState={utmErrorState}
            setUtmErrorState={setUtmErrorState}
          />
        }
        handleJob={async () => {
          const isError = utmData.some(item => item.value.length === 0)
          if (isError) {
            setUtmErrorState(
              utmData.map(item =>
                item.value.length === 0
                  ? { status: true, message: 'Please enter at least one value' }
                  : { status: false, message: '' }
              )
            )
            return
          }
          setState(utmData)
          setIsSelectorDialogOpen(false)
        }}
        jobButtonText={t('save')}
        closeButtonText={t('cancel')}
        width={600}
      />
    </div>
  )
}

interface UTMSelectorDialogProps {
  utmData: UTMSelectorStateType[]
  setUtmData: (utmData: UTMSelectorStateType[]) => void
  utmErrorState: FlowErrorType[]
  setUtmErrorState: (utmErrorState: FlowErrorType[]) => void
}

const UTMSelectorDialog: React.FC<UTMSelectorDialogProps> = ({
  utmData,
  setUtmData,
  utmErrorState,
  setUtmErrorState
}) => {
  const { t } = useLocale()
  const [usedOptions, setUsedOptions] = useState<utmDataOptions[]>([])

  useEffect(() => {
    setUsedOptions(utmData.filter((item, index) => index !== utmData.length - 1).map(item => item.key))
  }, [])

  const handleDeleteUTM = (index: number) => {
    setUtmErrorState(
      produce(utmErrorState, draft => {
        draft.splice(index, 1)
      })
    )
    setUtmData(
      produce(utmData, draft => {
        draft.splice(index, 1)
      })
    )
    if (index !== utmData.length - 1) {
      const exhaustedOptions = usedOptions.filter(option => option !== utmData[index].key)
      setUsedOptions(exhaustedOptions)
      return
    }
    if (utmData.length > 1) {
      const exhaustedOptions = usedOptions.filter(option => option !== utmData[index - 1].key)
      setUsedOptions(exhaustedOptions)
    }
  }

  return (
    <div className='flex flex-col space-y-4'>
      {utmData.map((utmItem, index) => (
        <UTMInput
          key={index}
          utmItem={utmItem}
          setUtmItem={(utm: UTMSelectorStateType) => {
            setUtmData(
              produce(utmData, draft => {
                draft[index] = utm
              })
            )
          }}
          index={index}
          selectionEnabled={index === utmData.length - 1}
          usedOptions={usedOptions}
          handleDeleteUTM={handleDeleteUTM}
          utmErrorState={utmErrorState[index]}
          setUtmErrorState={(utmError: FlowErrorType) => {
            setUtmErrorState(
              produce(utmErrorState, draft => {
                draft[index] = utmError
              })
            )
          }}
        />
      ))}

      {utmData?.length < utmDataDropdownOptions.length && (
        <button
          className='text-sm font-medium text-coal-dark px-3 py-2 rounded-xl border border-gray-light w-fit'
          onClick={() => {
            let exhaustedOptions: utmDataOptions[] = []
            if (utmData.length > 0) {
              exhaustedOptions = [...usedOptions, utmData[utmData.length - 1].key]
            }
            setUsedOptions(exhaustedOptions)
            const availableOptions = utmDataDropdownOptions.filter(option => !exhaustedOptions.includes(option.value))
            setUtmData([...utmData, { key: availableOptions[0].value, value: [] }])
            setUtmErrorState([...utmErrorState, { status: false, message: '' }])
          }}
        >
          <span className='flex flex-row items-center gap-2'>
            {t('add_another_option')}
            <Plus size={16} strokeWidth={2.5} />
          </span>
        </button>
      )}
    </div>
  )
}

interface UTMInputProps {
  utmItem: UTMSelectorStateType
  setUtmItem: (utmItem: UTMSelectorStateType) => void
  index: number
  selectionEnabled: boolean
  usedOptions: utmDataOptions[]
  handleDeleteUTM: (index: number) => void
  utmErrorState: FlowErrorType
  setUtmErrorState: (utmError: FlowErrorType) => void
}

const UTMInput: React.FC<UTMInputProps> = ({
  utmItem,
  setUtmItem,
  index,
  selectionEnabled,
  usedOptions,
  handleDeleteUTM,
  utmErrorState,
  setUtmErrorState
}) => {
  const { t } = useLocale()
  const [utmText, setUtmText] = useState<string>('')
  const [options, setOptions] = useState<UTMSelectorDropdownOptionType[]>(utmDataDropdownOptions)
  const [selectedIndex, setSelectedIndex] = useState<number>(0)

  const currentOption = utmDataDropdownOptions.find(option => option.value === utmItem.key)

  useEffect(() => {
    const availableOptions = utmDataDropdownOptions.filter(option => !usedOptions.includes(option.value))
    if (selectionEnabled) {
      setOptions(availableOptions)
      return
    }
    if (currentOption) {
      setOptions([currentOption])
    }
  }, [usedOptions])

  useEffect(() => {
    const index = options.findIndex(option => option.value === utmItem.key)
    if (index !== -1) {
      setSelectedIndex(index)
    }
  }, [options, currentOption])

  return (
    <div className='flex flex-col gap-2 w-full' key={`${utmItem?.key}`}>
      <div className={`border-t-[0.5px] my-6 h-0 -ml-4 -mr-4 ${index === 0 ? 'hidden' : ''}`}>
        <span className='relative bg-[#f0f0f0] rounded-lg text-[#949494] text-xs -top-[13px] left-4 px-1 py-[0.5]'>
          AND
        </span>
      </div>
      <div className='flex space-x-2'>
        <DropdownButton
          fullWidth
          options={options} //Filter the column values for not repeating the same filters.
          handleClick={(index: number) => {
            setUtmItem({
              key: options[index].value,
              value: utmItem.value
            })
          }}
          variant='outlined'
          size='large'
          selectedOption={selectedIndex}
          disabled={!selectionEnabled}
        />
        <div className='flex items-center text-coal-dark bg-gray-light rounded-lg px-6'>{'is'}</div>

        <button className='border border-gray-light rounded-lg px-4' onClick={() => handleDeleteUTM(index)}>
          <XCircle className='w-4 h-4 stroke-[2.5px]' />
        </button>
      </div>
      <div>
        <div className='w-full flex justify-center gap-2 items-end'>
          <InputTextField
            textFieldId={`utm-input-${index + 1}`}
            inputPlaceholder={t('add_utm_value', { type: currentOption?.label })}
            inputValue={utmText}
            handleChange={(event: React.ChangeEvent<HTMLInputElement>) => {
              setUtmText(event.target.value)
            }}
            handleKeyDown={(event: React.KeyboardEvent<HTMLInputElement>) => {
              if (event.key === 'Enter') {
                const text = utmText
                if (!text.length) return
                setUtmItem({
                  key: utmItem.key,
                  value: [...utmItem.value, text]
                })
                setUtmText('')
              }
            }}
            handleFocus={() => {
              setUtmErrorState({ status: false, message: '' })
            }}
          />
          <button
            className='bg-white rounded-lg px-4 py-2.5 text-coal-dark border border-gray-light cursor-pointer'
            onClick={() => {
              const text = utmText
              if (!text.length) return
              setUtmItem({
                key: utmItem.key,
                value: [...utmItem.value, text]
              })
              setUtmText('')
            }}
            disabled={!Boolean(utmText)}
          >
            <Plus className='w-4 h-4' strokeWidth={2.5} />
          </button>
        </div>

        <div className='flex flex-wrap w-full h-full gap-2 mt-3'>
          {utmItem?.value?.map((value: string, valueIndex: number) => (
            <Chip
              key={`${value}`}
              style={{
                borderRadius: '4px',
                border: '1px solid #F0F0F0',
                background: '#FAFAFA',
                fontSize: '14px',
                color: '#6e6e6e',
                height: '25px'
              }}
              size='medium'
              deleteIcon={<X className='stroke-[2.5px] text-[#6e6e6e]' />}
              onDelete={() => {
                setUtmItem({
                  key: utmItem.key,
                  value: utmItem.value.filter((_, index) => index !== valueIndex)
                })
              }}
              label={value}
            />
          ))}
        </div>
        {utmErrorState.status && <span className='text-red-dark text-sm'>{utmErrorState.message}</span>}
      </div>
    </div>
  )
}

export default UTMSelector
